import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import type { Workflow } from "../../../api/src/tasks/tasks.service";
import { Table, useTable } from "../components/Table/Table";
import {
  AssignedToFilter,
  multiSelectIncludes,
} from "../components/Table/filters";
import { useQueryCrmUsers } from "../firm/lib";

/**
 * A workflow row with the CRM user it is assigned to. The API's `Workflow`
 * has no `assignedUser` (APP-1653); this list resolves it from the firm's CRM
 * users.
 */
type WorkflowWithAssignedUser = Workflow & {
  assignedUser?: { id: number | string; name: string };
};

/** The CRM deep link for a workflow row, or `undefined` for another service. */
const workflowHref = (workflow: Workflow): string | undefined =>
  workflow.service === "wealthbox"
    ? `${process.env.REACT_APP_WEALTHBOX_APP_ROOT}/workflows/${workflow.id}`
    : workflow.service === "redtail"
      ? `${process.env.REACT_APP_REDTAIL_APP_ROOT}/workflows/${workflow.id}`
      : undefined;

/**
 * A workflow cell's text, linked to its CRM. A cell with no text shows no link
 * (APP-2244); the cell stays empty.
 */
const WorkflowCellLink = ({
  workflow,
  children,
}: {
  workflow: Workflow;
  children?: string | null;
}) => {
  const href = workflowHref(workflow);

  return typeof href === "undefined" || !children ? (
    <>{children}</>
  ) : (
    <a href={href} rel="noreferrer" target="_blank">
      {children}
    </a>
  );
};

const WorkflowList = ({ workflows }: { workflows: Workflow[] }) => {
  const { data: dataAssignees } = useQueryCrmUsers();

  const workflowsWithAssignedUsers = useMemo(
    (): WorkflowWithAssignedUser[] =>
      workflows.map((workflow) => ({
        ...workflow,
        assignedUser: dataAssignees?.data.find(
          (assignee) => assignee.id === workflow.assignedTo,
        ),
      })),
    [dataAssignees?.data, workflows],
  );

  const columnHelper = useMemo(
    () => createColumnHelper<WorkflowWithAssignedUser>(),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: (info) => (
          <WorkflowCellLink workflow={info.row.original}>
            {info.getValue()}
          </WorkflowCellLink>
        ),
        header: () => "Name",
      }),
      columnHelper.accessor("description", {
        cell: (info) => (
          <WorkflowCellLink workflow={info.row.original}>
            {info.getValue()}
          </WorkflowCellLink>
        ),
        header: () => "Description",
      }),
      columnHelper.accessor("activeStepName", {
        cell: (info) => info.getValue(),
        header: () => "Current Step",
      }),
      columnHelper.accessor((row) => row.assignedUser?.id.toString() ?? "", {
        id: "assignedUserId",
        cell: (info) => info.row.original.assignedUser?.name ?? "",
        header: () => "Assigned To",
        minSize: 100,
        filterFn: multiSelectIncludes,
        meta: {
          filterComponent: AssignedToFilter,
        },
      }),
      columnHelper.accessor("startedAt", {
        cell: (info) => info.getValue()?.toLocaleDateString() ?? "",
        header: () => "Start Date",
        enableColumnFilter: false,
      }),
    ],
    [columnHelper],
  );

  const { table } = useTable({
    columns,
    data: workflowsWithAssignedUsers,
    getRowId: (row) => row.id.toString(),
    initialState: {
      sorting: [{ id: "startedAt", desc: false }],
    },
  });

  return <Table table={table} />;
};

export default WorkflowList;
